import React from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Authenticated, NotAuthenticated } from '~/auth';
import { NavLink } from '~/lib/react-router-dom';

import { Alert, Box, Button, Card, Container, Toolbar } from '@mui/material';

import ErrorBoundary from '~/components/Common/ErrorBoundary';
import {
  AdGroupDetailsView,
  AdGroupListView,
  BidModificationHistory,
  CampaignDetailsView,
  CustomerCampaignListView,
  CustomerListView,
} from '~/components/GoogleAds';
import AdCreativeAIAssistantContainer from '~/components/GoogleAds/AdCreativeAIAssistantContainer';
import HotelBidFailureLog from '~/components/GoogleAdsHotelBidding/HotelBidFailureLog';
import HotelBidModificationHistory from '~/components/GoogleAdsHotelBidding/HotelBidModificationHistory';
import BusinessMainPage from '~/components/LeBusinessTraveller/pages/MainPage';
import usePromoAdminPermission from '~/components/Promo/hooks/usePromoPermission';
import ToursDepartureInsightsPage from '~/components/Tours/Pages/Lists/ToursDepartureInsightsPage';
import CreateOfferDeepLinkForm from '~/components/Users/CreateOfferDeepLinkForm';
import VendorInclusionsPage from '~/components/Vendors/Profile/VendorInclusionsPage';
import RoomTypesGroupsPageContainer from '~/components/Vendors/Properties/RoomTypesGroupsPageContainer';

import AskRichardUnifiedPage from '~/pages/AskRichardUnified/lazy';
import { legacyLensUrl } from '~/pages/AskRichardUnified/legacyLensUrl';
import { unifiedAskRichardPath } from '~/pages/AskRichardUnified/shellUrlState';
import CommercialOpsPage from '~/pages/CommercialOps/CommercialOpsPage';
import LensAccessGate from '~/pages/LeLens/LensAccessGate';
import AudienceAdminPage from '~/pages/Marketing/AudienceAdminPage';
import MarketingRouterRuleComparisonPage from '~/pages/Marketing/MarketingRouterRuleComparisonPage';
import MarketingRouterRuleDetailPage from '~/pages/Marketing/MarketingRouterRuleDetailPage';
import MarketingRouterRuleFormPage from '~/pages/Marketing/MarketingRouterRuleFormPage';
import LuxPlusScheduleDefaultsPage from '~/pages/Offers/LuxPlusScheduleDefaults/LuxPlusScheduleDefaultsPage';
import ShopPage from '~/pages/Shop/Page';
import VoucherDisplayConfigDetailPage from '~/pages/VoucherDisplayConfig/VoucherDisplayConfigDetailPage';
import QuickCreateVoucherPage from '~/pages/Vouchers/QuickCreateVoucherPage';
import VoucherAutoAssignRuleFormPage from '~/pages/Vouchers/VoucherAutoAssignRuleFormPage';
import VoucherAutoAssignRuleGroupPage from '~/pages/Vouchers/VoucherAutoAssignRuleGroupPage';
import VoucherFormPage from '~/pages/Vouchers/VoucherFormPage';

import { ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_OFFER, ITEM_TYPE_OFFLINE_FLIGHT } from '~/consts/order';
import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_PROMO_ADMIN_USER } from '~/consts/roles';

import useCanAccessLeLens from '~/hooks/useCanAccessLeLens';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import featureToggle from '~/utils/featureToggle';
import isProd from '~/utils/isProd';

import AccommodationBundleCreatePage from './Accommodation/Pages/Bundles/AccommodationBundleCreatePage';
import AccommodationBundleEditPage from './Accommodation/Pages/Bundles/AccommodationBundleEditPage';
import AccommodationCampaignCreatePage from './Accommodation/Pages/Campaigns/AccommodationCampaignCreatePage';
import AccommodationCampaignEditPage from './Accommodation/Pages/Campaigns/AccommodationCampaignEditPage';
import AccommodationCampaignsContainer from './Accommodation/Pages/Campaigns/AccommodationCampaignsContainer';
import AccommodationHomePage from './Accommodation/Pages/Home/AccommodationHomePage';
import AccommodationPropertyDetailsPage from './Accommodation/Pages/PropertyDetails/AccommodationPropertyDetailsPage';
import { AiChatEntry } from './AiChat';
import AmenitiesPageContainer from './Amenities/AmenitiesPageContainer';
import AmenityEdit from './Amenities/AmenityEdit';
import AmenityGroupEdit from './Amenities/AmenityGroupEdit';
import AttractionDetailPage from './Attractions/AttractionDetailPage';
import AwsJobsPage from './AwsJobs/Page';
import BedbankPropertiesEditPage from './Bedbank/Properties/Edit/Page';
import BedbankPropertiesPage from './Bedbank/Properties/Home/Page';
import { BookingPage } from './Bookings/BookingPage';
import ChannelMarkupCreatePage from './ChannelMarkup/ChannelMarkupCreatePage';
import ChannelMarkupCreateV2Page from './ChannelMarkup/ChannelMarkupCreateV2Page';
import AppChrome from './Common/AppChrome';
import Footer from './Common/Footer';
import PermissionedComponent from './Common/PermissionedComponent';
import Spotlight from './Common/Spotlight/Spotlight';
import { RouteScanner, SpotlightProvider } from './Common/Spotlight/SpotlightProvider';
import CurationPageContainer from './Content/CurationPageContainer';
import ContentToolsPage from './ContentTools/ContentToolsPage';
import CompaniesListPage from './CorporateEvents/pages/CompaniesListPage';
import CompanyFormPage from './CorporateEvents/pages/CompanyFormPage';
import EventFormPage from './CorporateEvents/pages/EventFormPage';
import EventsListPage from './CorporateEvents/pages/EventsListPage';
import CruiseBundlesCreate from './Cruises/pages/Bundles/BundlesCreate';
import CruiseBundlesEdit from './Cruises/pages/Bundles/BundlesEdit/BundlesEdit';
import CruiseLineEdit from './Cruises/pages/CruiseLines/CruiseLineEdit';
import CruiseLineOrdering from './Cruises/pages/CruiseLines/CruiseLineOrdering/CruiseLineOrdering';
import CruiseDealCrawlerCreate from './Cruises/pages/DealCrawlerPage/DealCrawlerCreate';
import DealCrawlerPageCreationConfirm from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/DealCrawlerPageCreationConfirm';
import DealCrawlerPageCreationSelection from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/DealCrawlerPageCreationSelection';
import {
  DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH,
  DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH,
} from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/paths';
import CruiseDealCrawlerView from './Cruises/pages/DealCrawlerPage/DealCrawlerView';
import DealPageCreate from './Cruises/pages/DealsPage/DealPageCreate';
import DealPageEdit from './Cruises/pages/DealsPage/DealPageEdit/DealPageEdit';
import DealPageOrdering from './Cruises/pages/DealsPage/DealPageOrdering';
import DealPageList from './Cruises/pages/DealsPage/components/DealPageList';
import CruisePageImages from './Cruises/pages/Home/CruisePageImages';
import CruisesPage from './Cruises/pages/Home/CruisesPage';
import CruisePageDetails from './Cruises/pages/Home/CruisesPageDetails';
import CruisesBookingLogsPage from './Cruises/pages/Home/components/Bookings/BookingLogsPage';
import CruisesBookingsPage from './Cruises/pages/Home/components/Bookings/BookingsPage';
import CruiseCabinCategoriesDetailsPage from './Cruises/pages/Home/components/CabinCategories/CabinCategoriesDetails';
import CabinCategoriesGroupOverviewPage from './Cruises/pages/Home/components/CabinCategoriesGroup/CabinCategoriesGroupOverview/CabinCategoriesGroupOverviewPage';
import CreateCabinCategoriesGroupPage from './Cruises/pages/Home/components/CabinCategoriesGroup/CreateCabinCategoriesGroup/CreateCabinCategoriesGroup';
import CruiseSailingDetailsPage from './Cruises/pages/Home/components/Sailings/SailingDetailsPage';
import CruiseInclusionsCreate from './Cruises/pages/Inclusions/InclusionsCreate';
import CruiseInclusionsEdit from './Cruises/pages/Inclusions/InclusionsEdit/InclusionsEdit';
import CruisePortsEditPage from './Cruises/pages/Ports/PortsEditPage';
import EditRateCodePage from './Cruises/pages/RateCodeManagement/EditRateCodePage';
import RateCodeManagementPage from './Cruises/pages/RateCodeManagement/RateCodeManagementPage';
import RateGroupingManagementPage from './Cruises/pages/RateGroupingManagement/RateGroupingManagementPage';
import ShipImageEditor from './Cruises/pages/Ships/ShipImageEditor';
import CsDashboardPage from './CsDashboard/CsDashboardPage';
import HeroPlannerScheduleEditorPage from './CustomerCommunication/HeroPlanner/Pages/HeroPlannerScheduleEditorPage';
import HeroPlannerSchedulesListPage from './CustomerCommunication/HeroPlanner/Pages/HeroPlannerSchedulesListPage';
import MarketingEmailsPageContainer from './CustomerCommunication/MarketingEmails/MarketingEmailsPageContainer';
import RenderMarketingEmailPage from './CustomerCommunication/MarketingEmails/RenderMarketingEmail';
import TranslationCatalogPage from './CustomerCommunication/TranslationCatalog/TranslationCatalogPage';
import TranslationOverviewPage from './CustomerCommunication/TranslationOverview/TranslationOverviewPage';
import CxDashboardPage from './CxDashboard/CxDashboardPage';
import EmailPage from './Emails/EmailPage';
import EmailPageContainer from './Emails/EmailPageContainer';
import RenderEmail from './Emails/RenderEmail';
import ExperiencesPage from './Experiences';
import AttractionImageEditor from './Experiences/Attractions/AttractionImageEditor';
import FinancePageContainer from './Finance/FinancePageContainer';
import FinanceOfferDetailContainer from './Finance/OfferDetail/OfferDetail';
import VendorBookingPaymentsContainer from './Finance/OfferEdit/VendorBookingPaymentsContainer';
import VendorOfferPaymentsContainer from './Finance/OfferEdit/VendorOfferPaymentsContainer';
import CreditReservationDetailContainer from './Flights/CreditReservationDetailContainer';
import CreditReservationEditContainer from './Flights/CreditReservationEditContainer';
import CreditReservationListContainer from './Flights/CreditReservationListContainer';
import FlightsDealsEdit from './Flights/FlightDeals/FlightDealsEdit';
import FlightsCampaignsEdit from './Flights/FlightsCampaigns/FlightsCampaignsEdit';
import { FlightsLogsViewer } from './Flights/FlightsLogsViewer/FlightsLogsViewer';
import FlightsPage from './Flights/FlightsPageContainer';
import HealthPage from './Health/HealthPage';
import IndexPage from './IndexPage';
import InvoiceToolsPageContainer from './InvoiceTools/InvoiceToolsPageContainer';
import LereAdminPageContainer from './LERE/LereAdminPageContainer';
import BusinessTabContainer from './LeBusinessTraveller/pages/BusinessDetailsPage/TabContainer';
import LoyaltyPageContainer from './Loyalty/LoyaltyPageContainer';
import BadgesContainer from './Marketing/Badges/BadgesContainer';
import BadgesDetailContainer from './Marketing/Badges/BadgesDetailContainer';
import CompedPackagesDetailContainer from './Marketing/CompedPackages/CompedPackagesDetailContainer';
import MarketingPage from './Marketing/MarketingPage';
import AccountAssetDetailPage from './Offers/Detail/OfferBuilder/Account/AccountAssetDetailPage';
import AccountAssetsListPage from './Offers/Detail/OfferBuilder/Account/AccountAssetsListPage';
import AccountOpportunitiesListPage from './Offers/Detail/OfferBuilder/Account/AccountOpportunitiesListPage';
import AccountRelatedPage from './Offers/Detail/OfferBuilder/Account/AccountRelatedPage';
import AccountShell from './Offers/Detail/OfferBuilder/Account/AccountShell';
import OfferBuilderAssetsPage from './Offers/Detail/OfferBuilder/Assets/OfferBuilderAssetsPage';
import OfferBuilderCMChecklistPage from './Offers/Detail/OfferBuilder/CMChecklist/OfferBuilderCMChecklistPage';
import OfferBuilderContentPage from './Offers/Detail/OfferBuilder/Content/OfferBuilderContentPage';
import OfferBuilderDealInformationPage from './Offers/Detail/OfferBuilder/DealInformation/OfferBuilderDealInformationPage';
import OfferBuilderPricingToolPage from './Offers/Detail/OfferBuilder/OfferBuilderPricingToolPage';
import OfferBuilderShell from './Offers/Detail/OfferBuilder/OfferBuilderShell';
import OfferBuilderRatesAndInventoryPage from './Offers/Detail/OfferBuilder/RatesAndInventory/OfferBuilderRatesAndInventoryPage';
import OfferDetailContainer from './Offers/Detail/OfferDetailContainer';
import BadgesEditContainer from './Offers/Edit/BadgesEditContainer';
import BundlePackagesEditContainer from './Offers/Edit/BundlePackagesEditContainer';
import ExperiencesEditContainer from './Offers/Edit/ExperiencesEditContainer';
import ImagesEditContainer from './Offers/Edit/ImagesEditContainer';
import NewScheduleContainer from './Offers/Edit/NewScheduleContainer';
import OfferEditContainer from './Offers/Edit/OfferEditContainer';
import OfferInventoryContainer from './Offers/Edit/OfferInventoryContainer';
import RoomPolicyEditContainer from './Offers/Edit/RoomPoliciesEditContainer';
import ScheduleEditContainer from './Offers/Edit/ScheduleEditContainer';
import OfferSearchOrderingContainer from './Offers/Edit/SearchOrderingContainer';
import TravellerDetailRequirementsEditPage from './Offers/Edit/TravellerDetailRequirements';
import VideosEditContainer from './Offers/Edit/VideosEditContainer';
import OffersPage from './Offers/Home/OffersPage';
import OpportunityContainer from './Opportunity/OpportunityContainer';
import ExamplePage from './Page/Example/ExamplePage';
import ScheduledPriceSummaryJobDetails from './PriceParrot/ScheduledPriceSummaryJobDetails';
import LoginPage from './Profile/LoginPage';
import ProfilePage from './Profile/ProfilePage';
import OrderEditContainer from './Purchases/Edit/OrderEditContainer';
import PurchasesPageBookingNumberContainer from './Purchases/Home/PurchasesPageBookingNumberContainer';
import PurchasesPageContainer from './Purchases/Home/PurchasesPageContainer';
import OrderAddNotePage from './Purchases/OrderDetail/OrderAddNotePage';
import OrderAtolPageContainer from './Purchases/OrderDetail/OrderAtolPageContainer';
import OrderBookingContainer from './Purchases/OrderDetail/OrderBookingPageContainer';
import OrderGiftEditPage from './Purchases/OrderDetail/OrderGiftEditPage';
import OrderPaymentSurcharge from './Purchases/OrderDetail/OrderPaymentSurcharge';
import SupportAssistantPage from './SupportAssistant/pages/Home/SupportAssistantPage';
import UpdateMonitor from './System/UpdateMonitor';
import TagDataExport from './Tags/TagDataExport';
import TourDealPageCreate from './Tours/Pages/Deals/DealPageCreate';
import TourDealPageEdit from './Tours/Pages/Deals/DealPageEdit';
import TourDealsPage from './Tours/Pages/Deals/TourDealsPage';
import TourSupplierPage from './Tours/Pages/Lists/TourSupplierPage';
import ToursLEListPage from './Tours/Pages/Lists/ToursLEListPage';
import ToursManifestPage from './Tours/Pages/Lists/ToursManifestPage';
import ToursNotificationsPage from './Tours/Pages/Lists/ToursNotificationsPage';
import ToursSuppliersPage from './Tours/Pages/Lists/ToursSuppliersPage';
import ToursTTCListPage from './Tours/Pages/Lists/ToursTTCListPage';
import TourDetailsPage from './Tours/Pages/TourDetailsPage';
import { SUPPLIERS_PATH, isNativeTourModelEnabled } from './Tours/nativeTourModel';
import TripPlannerChatPage from './TripPlanner/TripPlannerChatPage';
import { tripPlannerChatTabs } from './TripPlanner/tripPlannerChatTabs';
import CreditsContainer from './Users/Credits/CreditsContainer';
import FindByPhone from './Users/FindByPhone/FindByPhone';
import UserHistory from './Users/History/UserHistory';
import AddItemPageContainer from './Users/Orders/AddItem/AddItemPageContainer';
import AtolCertificateDetailPage from './Users/Orders/Atol/AtolCertificateDetailPage';
import AtolCertificateEditPage from './Users/Orders/Atol/AtolCertificateEditPage';
import AtolPage from './Users/Orders/Atol/AtolPage';
import NewOrderPageContainer from './Users/Orders/NewOrder/NewOrderPageContainer';
import UpdateInsuranceContainer from './Users/Orders/UpdateInsurance';
import UserPage from './Users/UserPage';
import UsersPageContainer from './Users/UsersPageContainer';
import { VendorAddonDetails } from './Vendors/Addons/AddonDetails';
import { VendorAddonEdit } from './Vendors/Addons/AddonEdit';
import EditChainSettingsPage from './Vendors/ChainSettings/EditChainSettingsPage';
import ChainDetailPage from './Vendors/Chains/ChainDetailPage';
import VendorsPageContainer from './Vendors/Home/VendorsPageContainer';
import VendorsPartnerCentralPage from './Vendors/PartnerCentral/VendorsPartnerCentralPage';
import ChainsDetailContainer from './Vendors/Profile/ChainsDetailContainer';
import VendorsDetailContainer from './Vendors/Profile/VendorsDetailContainer';
import CuratedContentEditPage from './Vendors/Properties/CuratedContentEditPage';
import ImagesEditPage from './Vendors/Properties/ImagesEditPage';
import PropertyCommissionableTaxesContentEditPage from './Vendors/Properties/PropertyCommissionableTaxesContentEditPage';
import PropertyEditContainer from './Vendors/Properties/PropertyEditContainer';
import PropertyNewContainer from './Vendors/Properties/PropertyNewContainer';
import PropertyPageContainer from './Vendors/Properties/PropertyPageContainer';
import PropertyReviewsEditContainer from './Vendors/Properties/PropertyReviewsEditContainer';
import PropertyTaxesContentEditPage from './Vendors/Properties/PropertyTaxesContentEditPage';
import PropertyTaxesFeesEditPage from './Vendors/Properties/PropertyTaxesFeesEditPage';
import RoomTypeDatesEditContainer from './Vendors/Properties/RoomTypeDatesEditContainer';
import RoomTypeEditContainer from './Vendors/Properties/RoomTypeEditContainer';
import RoomTypesEditContainer from './Vendors/Properties/RoomTypesEditContainer';
import VideosEditPage from './Vendors/Properties/VideosEditPage';
import RatePlanEditContainer from './Vendors/RatePlans/RatePlanEditContainer';
import RatePlanInclusionsPage from './Vendors/RatePlans/RatePlanInclusionsPage';
import RatePlanNewContainer from './Vendors/RatePlans/RatePlanNewContainer';
import RatePlanPageContainer from './Vendors/RatePlans/RatePlanPageContainer';
import RatePlansGroupEditContainer from './Vendors/RatePlansGroups/RatePlansGroupEditContainer';
import RoomTypesGroupEditContainer from './Vendors/RoomTypesGroups/RoomTypesGroupEditContainer';
import TourEditContainer from './Vendors/Tours/TourEditContainer';
import TourLegsEditContainer from './Vendors/Tours/TourLegsEditContainer';
import TourNewContainer from './Vendors/Tours/TourNewContainer';
import TourOptionDatesEditContainer from './Vendors/Tours/TourOptionDatesEditContainer';
import TourOptionsEditContainer from './Vendors/Tours/TourOptionsEditContainer';
import TourPageContainer from './Vendors/Tours/TourPageContainer';
import TourReviewsEditContainer from './Vendors/Tours/TourReviewsEditContainer';
import {
  AIImageManagerPage,
  AccountDeletionsPage,
  AdminUsersPageContainer,
  AgenciesPage,
  AiQaDashboardPage,
  AuditLogSearch,
  BannerUploadPage,
  BriefCasePage,
  BriefCaseTemplatePage,
  BulkCreditAllocationPage,
  BulkVccReplacementPage,
  CSAnalysisPage,
  CSNewHireQuizPage,
  CogsAdjustmentRequestsPage,
  CreditReversalsPage,
  CustomOffersCreatePage,
  CustomOffersEditPage,
  EmailAIKillSwitchesPage,
  EmailDeliveryFailuresPage,
  FindSimilarAccountsPage,
  ImageUploaderUtilityPage,
  LabPage,
  LabV2Page,
  LeDataGalleryBackfillPage,
  LeDataPage,
  MediaAssetCreatePage,
  MediaAssetDetailPage,
  MediaAssetsPage,
  MediaPackageDetailPage,
  MediaPackagesPage,
  OpenAIPage,
  OrderDetailContainer,
  OrderFinancialRecordsPage,
  PackagesEditContainer,
  PermissionsContainer,
  PromoDetailContainerV3,
  ReservationRemediation,
  SaleOwnershipSwapsPage,
  SalesFxHedging,
  SalesPerformanceDashboardPage,
  Settings,
  SiteBannerCampaignCreate,
  SiteBannerCampaignEdit,
  SiteBannerCampaignsPage,
  StoplistPage,
  StructuredFacilitiesPage,
  StructuredFinePrintPage,
  SystemPageContainer,
  TermsAndConditionsPage,
  TrainingAccountProvisioningPage,
  VendorBillingRulesPage,
} from './lazyRoutes';

type AppRouteElementProps = {
  routePath: string;
  component?: React.ComponentType<any>;
  render?: (props: any) => React.ReactNode;
};

function AppRouteElement({ routePath, component: Component, render }: AppRouteElementProps) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const routeProps = {
    match: {
      params,
      path: routePath,
      url: location.pathname,
      isExact: true,
    },
    location,
    history: {
      push: (to, state) => navigate(to, { state }),
      replace: (to, state) => navigate(to, { replace: true, state }),
      go: (delta: number) => navigate(delta),
      goBack: () => navigate(-1),
    },
  };

  if (Component) {
    return <Component {...routeProps} />;
  }

  return <>{render?.(routeProps) ?? null}</>;
}

const TOURS_SUB_NAVIGATIONS: Array<App.NavigationLink> = [
  {
    href: '/tours/lists/le',
    label: 'LE Tours',
  },
  {
    href: '/tours/lists/ttc',
    label: 'TTC Tours',
  },
  // Suppliers are part of the tours build model. The pages are reachable by URL everywhere;
  // only the nav link is held back to staging, as with the pricing-and-inventory tab.
  ...(isNativeTourModelEnabled()
    ? [
        {
          href: SUPPLIERS_PATH,
          label: 'Suppliers',
        },
      ]
    : []),
  {
    href: '/tours/departures/insights',
    label: 'Departures insights',
  },
  {
    href: '/tours/deals',
    label: 'Deals',
  },
  {
    href: '/tours/manifests',
    label: 'Departure Manifests',
  },
  {
    href: '/tours/notifications',
    label: 'DMC Notifications',
  },
];

function isToursNavigationSelected(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Lens links used to bypass the analytics workspace entirely. That left a saved
 * `/le-lens/:lensId` link without the persistent askRichard rail identity the
 * reader sees everywhere else in the workspace. Keep the old URLs valid, but
 * open them inside the single shell so the logo and navigation do not change
 * from one Lens entry point to another.
 */
function LensWorkspaceRedirect() {
  const { lensId } = useParams<{ lensId?: string }>();
  const location = useLocation();
  const destination = legacyLensUrl(lensId, location.search, location.hash);

  return (
    <LensAccessGate>
      <Navigate replace to={destination} />
    </LensAccessGate>
  );
}

/**
 * The top nav and Spotlight already send an eligible user straight into the unified shell
 * (navigationLinkHrefForUser). A typed or bookmarked /ask-richard should land the same reader in
 * the same place instead of opening the separate, older chat page beside it. A non-eligible user
 * sees no change: LeDataPage is still the only askRichard surface they can reach.
 */
function AskRichardFrontDoor() {
  const canAccess = useCanAccessLeLens();
  const { search } = useLocation();

  if (canAccess) {
    return <Navigate replace to={unifiedAskRichardPath(search)} />;
  }

  return <LeDataPage />;
}

export default function MasterPage() {
  const { tenant } = useCurrentTenant();
  const location = useLocation();
  const isPromoAdmin = usePromoAdminPermission();
  const isProduction = isProd();
  const isAccountViewEnabled = featureToggle.availableToShow('ACCOUNT_VIEW_ENABLED');

  const { pathname } = location;
  const isAskRichardRoute =
    pathname === '/ask-richard' ||
    pathname.startsWith('/ask-richard/') ||
    pathname === '/ask-richard-unified' ||
    pathname.startsWith('/ask-richard-unified/');
  const isLensWorkspaceRoute = pathname === '/le-lens' || pathname.startsWith('/le-lens/');
  // hide global AI chat in LE Lens
  const hideGlobalChatEntry =
    pathname === '/ask-richard-unified' || pathname.startsWith('/ask-richard-unified/') || isLensWorkspaceRoute;

  const promoAdminOnlyDenied = (
    <Container sx={{ mt: 2 }}>
      <Alert severity="error">
        This page is in testing and only available to users with the {ROLE_PROMO_ADMIN_USER} role. For access, contact
        the growth team.
      </Alert>
    </Container>
  );

  const billingRulesDenied = (
    <Container sx={{ mt: 2 }}>
      <Alert severity="error">
        Vendor Billing Rules is only available to admin and employee users. For access, contact the data team.
      </Alert>
    </Container>
  );

  const renderToursContent = (Component: React.ComponentType<any>) => (props: any) => (
    <>
      <Container sx={{ mb: 2 }}>
        <Card variant="outlined">
          <Toolbar>
            {TOURS_SUB_NAVIGATIONS.map((nav) => {
              const isSelected = isToursNavigationSelected(pathname, nav.href);

              return (
                <Button
                  key={nav.href}
                  component={NavLink}
                  to={nav.href}
                  color={isSelected ? 'primary' : 'secondary'}
                  variant={isSelected ? 'contained' : 'text'}
                  size="large"
                  sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                >
                  {nav.label}
                </Button>
              );
            })}
          </Toolbar>
        </Card>
      </Container>

      <Component {...props} />
    </>
  );

  return (
    <Box
      className="MasterPage"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        ...(isAskRichardRoute
          ? {
              height: '100dvh',
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      <Helmet titleTemplate="%s" defaultTitle={`Admin Portal | ${tenant.title}`} />

      <AppChrome tenant={tenant}>
        <UpdateMonitor />

        <Box
          flexGrow={1}
          sx={
            isAskRichardRoute
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }
              : undefined
          }
        >
          <Authenticated>
            <AiChatEntry hidden={hideGlobalChatEntry}>
              <SpotlightProvider>
                <Spotlight />
                <RouteScanner>
                  <Routes>
                    <Route path="/" element={<AppRouteElement routePath="/" component={IndexPage} />} />
                    <Route path="/curation" element={<Navigate to="/curation/offer-list-order" replace />} />
                    <Route path="/search" element={<Navigate to="/search/search-dashboard" replace />} />
                    <Route path="/search-curation" element={<Navigate to="/search/search-dashboard" replace />} />
                    <Route
                      path="/curation/search-dashboard"
                      element={<Navigate to="/search/search-dashboard" replace />}
                    />
                    <Route path="/curation/search-ranking" element={<Navigate to="/search/search-ranking" replace />} />
                    <Route
                      path="/curation/blocked-offers"
                      element={<Navigate to="/marketing/offer-blocklist" replace />}
                    />
                    <Route
                      path="/search/blocked-offers"
                      element={<Navigate to="/marketing/offer-blocklist" replace />}
                    />
                    <Route
                      path="/curation/property-location-override"
                      element={<Navigate to="/search/property-location-override" replace />}
                    />
                    <Route
                      path="/curation/restore-search-dashboard"
                      element={<Navigate to="/search/restore-search-dashboard" replace />}
                    />
                    <Route
                      path="/curation/:tab"
                      element={<AppRouteElement routePath="/curation/:tab" component={CurationPageContainer} />}
                    />
                    <Route path="/search-curation/nls" element={<Navigate to="/search/analytics" replace />} />
                    <Route
                      path="/search-curation/:tab"
                      element={
                        <AppRouteElement
                          routePath="/search-curation/:tab"
                          render={({ match }) => <Navigate to={`/search/${match.params.tab}`} replace />}
                        />
                      }
                    />
                    <Route
                      path="/search/:tab"
                      element={<AppRouteElement routePath="/search/:tab" component={CurationPageContainer} />}
                    />
                    <Route
                      path="/experiences"
                      element={<AppRouteElement routePath="/experiences" component={ExperiencesPage} />}
                    />
                    <Route
                      path="/experience/:id_experience"
                      element={<AppRouteElement routePath="/experience/:id_experience" component={ExperiencesPage} />}
                    />
                    {isAccountViewEnabled && (
                      <Route
                        path="/accounts/:id_account"
                        element={<AppRouteElement routePath="/accounts/:id_account" component={AccountShell} />}
                      >
                        <Route
                          index
                          element={<AppRouteElement routePath="/accounts/:id_account" component={AccountRelatedPage} />}
                        />
                        <Route
                          path="opportunities"
                          element={
                            <AppRouteElement
                              routePath="/accounts/:id_account/opportunities"
                              component={AccountOpportunitiesListPage}
                            />
                          }
                        />
                        <Route
                          path="assets"
                          element={
                            <AppRouteElement
                              routePath="/accounts/:id_account/assets"
                              component={AccountAssetsListPage}
                            />
                          }
                        />
                        <Route
                          path="assets/:assetId"
                          element={
                            <AppRouteElement
                              routePath="/accounts/:id_account/assets/:assetId"
                              component={AccountAssetDetailPage}
                            />
                          }
                        />
                      </Route>
                    )}
                    <Route
                      path="/offers/:id_offer/offer-builder"
                      element={
                        <AppRouteElement routePath="/offers/:id_offer/offer-builder" component={OfferBuilderShell} />
                      }
                    >
                      <Route index element={<Navigate to="content" replace />} />
                      <Route
                        path="assets"
                        element={
                          <AppRouteElement
                            routePath="/offers/:id_offer/offer-builder/assets"
                            component={OfferBuilderAssetsPage}
                          />
                        }
                      />
                      <Route
                        path="cm-checklist"
                        element={
                          <AppRouteElement
                            routePath="/offers/:id_offer/offer-builder/cm-checklist"
                            component={OfferBuilderCMChecklistPage}
                          />
                        }
                      />
                      <Route
                        path="content"
                        element={
                          <AppRouteElement
                            routePath="/offers/:id_offer/offer-builder/content"
                            component={OfferBuilderContentPage}
                          />
                        }
                      />
                      <Route
                        path="deal-information"
                        element={
                          <AppRouteElement
                            routePath="/offers/:id_offer/offer-builder/deal-information"
                            component={OfferBuilderDealInformationPage}
                          />
                        }
                      />
                      <Route
                        path="hotel-proposal-pricing-tool"
                        element={
                          <AppRouteElement
                            routePath="/offers/:id_offer/offer-builder/hotel-proposal-pricing-tool"
                            component={OfferBuilderPricingToolPage}
                          />
                        }
                      />
                      <Route
                        path="rates-and-inventory"
                        element={
                          <AppRouteElement
                            routePath="/offers/:id_offer/offer-builder/rates-and-inventory"
                            component={OfferBuilderRatesAndInventoryPage}
                          />
                        }
                      />
                    </Route>
                    <Route
                      path="/offers/:id_offer"
                      element={<AppRouteElement routePath="/offers/:id_offer" component={OfferDetailContainer} />}
                    />
                    <Route
                      path="/offers/lux-plus-schedule-defaults"
                      element={
                        <AppRouteElement
                          routePath="/offers/lux-plus-schedule-defaults"
                          component={LuxPlusScheduleDefaultsPage}
                        />
                      }
                    />
                    <Route path="/offers" element={<AppRouteElement routePath="/offers" component={OffersPage} />} />
                    <Route path="/content-tools" element={<Navigate to="/ai-tools" replace />} />
                    <Route
                      path="/ai-tools"
                      element={<AppRouteElement routePath="/ai-tools" component={ContentToolsPage} />}
                    />
                    <Route
                      path="/trip-planner"
                      element={<AppRouteElement routePath="/trip-planner" component={TripPlannerChatPage} />}
                    />
                    <Route path="/lab" element={<AppRouteElement routePath="/lab" component={LabPage} />} />
                    <Route path="/labv2" element={<Navigate to="/labv2/campaigns" replace />} />
                    {['campaigns', 'always-on', 'audiences', 'feature-flags'].map((tab) => (
                      <Route
                        key={tab}
                        path={`/labv2/${tab}`}
                        element={<AppRouteElement routePath="/labv2" component={LabV2Page} />}
                      />
                    ))}
                    <Route
                      path="/labv2/campaigns/:campaignId"
                      element={<AppRouteElement routePath="/labv2" component={LabV2Page} />}
                    />
                    <Route
                      path="/labv2/audiences/:audienceId"
                      element={<AppRouteElement routePath="/labv2" component={LabV2Page} />}
                    />
                    <Route path="/openai" element={<AppRouteElement routePath="/openai" component={OpenAIPage} />} />
                    <Route path="/nls" element={<Navigate to="/search/analytics" replace />} />
                    <Route
                      path="/ask-richard/shared/:sessionId"
                      element={<AppRouteElement routePath="/ask-richard/shared/:sessionId" component={LeDataPage} />}
                    />
                    <Route
                      path="/ask-richard"
                      element={<AppRouteElement routePath="/ask-richard" component={AskRichardFrontDoor} />}
                    />
                    <Route path="/data-chatbot" element={<Navigate to="/ask-richard" replace />} />
                    <Route
                      path="/ask-richard-unified"
                      element={
                        <AppRouteElement
                          routePath="/ask-richard-unified"
                          render={(props) => (
                            <LensAccessGate>
                              <ErrorBoundary fallbackTitle="askRichard">
                                <AskRichardUnifiedPage {...props} />
                              </ErrorBoundary>
                            </LensAccessGate>
                          )}
                        />
                      }
                    />
                    <Route
                      path="/le-lens"
                      element={<AppRouteElement routePath="/le-lens" component={LensWorkspaceRedirect} />}
                    />
                    <Route
                      path="/le-lens/:lensId"
                      element={<AppRouteElement routePath="/le-lens/:lensId" component={LensWorkspaceRedirect} />}
                    />
                    <Route
                      path="/attractions/:attractionId"
                      element={
                        <AppRouteElement routePath="/attractions/:attractionId" component={AttractionDetailPage} />
                      }
                    />
                    <Route
                      path="/attractions/:attractionId/image"
                      element={
                        <AppRouteElement
                          routePath="/attractions/:attractionId/image"
                          component={AttractionImageEditor}
                        />
                      }
                    />
                    <Route path="/shop" element={<AppRouteElement routePath="/shop" component={ShopPage} />} />
                    <Route
                      path="/trip-planner/:chatId"
                      element={<AppRouteElement routePath="/trip-planner/:chatId" component={TripPlannerChatPage} />}
                    />
                    {tripPlannerChatTabs.map((tab) => (
                      <Route
                        key={tab.path}
                        path={tab.path}
                        element={<AppRouteElement routePath={tab.path} component={TripPlannerChatPage} />}
                      />
                    ))}
                    <Route
                      path="/briefcase"
                      element={<AppRouteElement routePath="/briefcase" component={BriefCasePage} />}
                    />
                    <Route
                      path="/briefcase/:jobId"
                      element={<AppRouteElement routePath="/briefcase/:jobId" component={BriefCasePage} />}
                    />
                    <Route
                      path="/briefcase-template"
                      element={<AppRouteElement routePath="/briefcase-template" component={BriefCaseTemplatePage} />}
                    />
                    <Route
                      path="/edit-offers/:id_offer"
                      element={<AppRouteElement routePath="/edit-offers/:id_offer" component={OfferEditContainer} />}
                    />

                    <Route
                      path="/edit-offers/:vertical/:id_offer/search-orderings"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:vertical/:id_offer/search-orderings"
                          component={OfferSearchOrderingContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/schedules/:brand"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/schedules/:brand"
                          component={ScheduleEditContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/schedules"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/schedules"
                          component={NewScheduleContainer}
                        />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/bundle-packages"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/bundle-packages"
                          component={BundlePackagesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/packages"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/packages"
                          component={PackagesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/images"
                      element={
                        <AppRouteElement routePath="/edit-offers/:id_offer/images" component={ImagesEditContainer} />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/videos"
                      element={
                        <AppRouteElement routePath="/edit-offers/:id_offer/videos" component={VideosEditContainer} />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/room-policies"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/room-policies"
                          component={RoomPolicyEditContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/structured-fine-print"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/structured-fine-print"
                          component={StructuredFinePrintPage}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/structured-facilities"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/structured-facilities"
                          component={StructuredFacilitiesPage}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/badges"
                      element={
                        <AppRouteElement routePath="/edit-offers/:id_offer/badges" component={BadgesEditContainer} />
                      }
                    />
                    <Route
                      path="/edit-offers/:id_offer/offer-inventory"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/offer-inventory"
                          component={OfferInventoryContainer}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/traveller-detail-requirements"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/traveller-detail-requirements"
                          component={TravellerDetailRequirementsEditPage}
                        />
                      }
                    />

                    <Route
                      path="/edit-offers/:id_offer/experiences"
                      element={
                        <AppRouteElement
                          routePath="/edit-offers/:id_offer/experiences"
                          component={ExperiencesEditContainer}
                        />
                      }
                    />

                    <Route path="/tours" element={<Navigate to="/tours/lists/le" replace />} />
                    <Route
                      path="/tours/lists/le"
                      element={
                        <AppRouteElement routePath="/tours/lists/le" render={renderToursContent(ToursLEListPage)} />
                      }
                    />
                    <Route
                      path="/tours/lists/ttc"
                      element={
                        <AppRouteElement routePath="/tours/lists/ttc" render={renderToursContent(ToursTTCListPage)} />
                      }
                    />
                    <Route
                      path="/tours/lists/suppliers"
                      element={
                        <AppRouteElement
                          routePath="/tours/lists/suppliers"
                          render={renderToursContent(ToursSuppliersPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/lists/suppliers/:supplier_id"
                      element={
                        <AppRouteElement
                          routePath="/tours/lists/suppliers/:supplier_id"
                          render={renderToursContent(TourSupplierPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/deals"
                      element={<AppRouteElement routePath="/tours/deals" render={renderToursContent(TourDealsPage)} />}
                    />
                    <Route
                      path="/tours/deals/create"
                      element={
                        <AppRouteElement
                          routePath="/tours/deals/create"
                          render={renderToursContent(TourDealPageCreate)}
                        />
                      }
                    />
                    <Route
                      path="/tours/deals/:id/edit"
                      element={
                        <AppRouteElement
                          routePath="/tours/deals/:id/edit"
                          render={renderToursContent(TourDealPageEdit)}
                        />
                      }
                    />
                    <Route
                      path="/tours/departures/insights"
                      element={
                        <AppRouteElement
                          routePath="/tours/departures/insights"
                          render={renderToursContent(ToursDepartureInsightsPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/manifests"
                      element={
                        <AppRouteElement routePath="/tours/manifests" render={renderToursContent(ToursManifestPage)} />
                      }
                    />
                    <Route
                      path="/tours/notifications"
                      element={
                        <AppRouteElement
                          routePath="/tours/notifications"
                          render={renderToursContent(ToursNotificationsPage)}
                        />
                      }
                    />
                    <Route
                      path="/tours/details/:tour_id/*"
                      element={
                        <AppRouteElement
                          routePath={'/tours/details/:tour_id/*'}
                          render={renderToursContent(TourDetailsPage)}
                        />
                      }
                    />
                    {/* Backwards compatibility for the Chrome Extension */}
                    <Route
                      path="/tours/:tour_id"
                      element={
                        <AppRouteElement
                          routePath="/tours/:tour_id"
                          render={({ match }) => <Navigate to={`/tours/details/${match.params.tour_id}`} replace />}
                        />
                      }
                    />

                    <Route
                      path="/aws/jobs"
                      element={<AppRouteElement routePath="/aws/jobs" component={AwsJobsPage} />}
                    />
                    <Route
                      path="/bedbank/properties"
                      element={<AppRouteElement routePath="/bedbank/properties" component={BedbankPropertiesPage} />}
                    />
                    <Route
                      path="/bedbank/properties/:id"
                      element={
                        <AppRouteElement routePath="/bedbank/properties/:id" component={BedbankPropertiesEditPage} />
                      }
                    />
                    <Route path="/flights" element={<AppRouteElement routePath="/flights" component={FlightsPage} />} />
                    <Route
                      path="/flights/credit-reservation"
                      element={
                        <AppRouteElement
                          routePath="/flights/credit-reservation"
                          component={CreditReservationListContainer}
                        />
                      }
                    />
                    <Route
                      path="/flights/credit-reservation/:id/details"
                      element={
                        <AppRouteElement
                          routePath="/flights/credit-reservation/:id/details"
                          component={CreditReservationDetailContainer}
                        />
                      }
                    />
                    <Route
                      path="/flights/credit-reservation/:id/edit"
                      element={
                        <AppRouteElement
                          routePath="/flights/credit-reservation/:id/edit"
                          component={CreditReservationEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/flights/flight-deal/:id/edit"
                      element={
                        <AppRouteElement routePath="/flights/flight-deal/:id/edit" component={FlightsDealsEdit} />
                      }
                    />
                    <Route
                      path="/flights/campaigns/:id"
                      element={<AppRouteElement routePath="/flights/campaigns/:id" component={FlightsCampaignsEdit} />}
                    />
                    <Route
                      path="/flights/xml-logs-viewer/:id"
                      element={
                        <AppRouteElement routePath="/flights/xml-logs-viewer/:id" component={FlightsLogsViewer} />
                      }
                    />

                    <Route path="/users-list" element={<Navigate to="/system" replace />} />
                    <Route path="/users-list/stop-list/:id?" element={<Navigate to="/system/stop-list" replace />} />
                    <Route
                      path="/agencies"
                      element={<AppRouteElement routePath="/agencies" render={() => <AgenciesPage />} />}
                    />
                    <Route
                      path="/agencies/agencies"
                      element={<AppRouteElement routePath="/agencies/agencies" render={() => <AgenciesPage />} />}
                    />
                    <Route
                      path="/agencies/agency-submission"
                      element={
                        <AppRouteElement routePath="/agencies/agency-submission" render={() => <AgenciesPage />} />
                      }
                    />
                    <Route
                      path="/agencies/affiliations"
                      element={<AppRouteElement routePath="/agencies/affiliations" render={() => <AgenciesPage />} />}
                    />
                    <Route
                      path="/users-list/agency-submission"
                      element={
                        <AppRouteElement routePath="/users-list/agency-submission" component={UsersPageContainer} />
                      }
                    />
                    <Route
                      path="/users-list/agency-submission/:id"
                      element={
                        <AppRouteElement routePath="/users-list/agency-submission/:id" component={UsersPageContainer} />
                      }
                    />
                    <Route
                      path="/users-list/agencies"
                      element={<AppRouteElement routePath="/users-list/agencies" component={UsersPageContainer} />}
                    />
                    <Route
                      path="/users-list/agencies/:id"
                      element={<AppRouteElement routePath="/users-list/agencies/:id" component={UsersPageContainer} />}
                    />
                    <Route
                      path="/users-list/affiliations"
                      element={<AppRouteElement routePath="/users-list/affiliations" component={UsersPageContainer} />}
                    />
                    <Route
                      path="/users-list/affiliations/:id"
                      element={
                        <AppRouteElement routePath="/users-list/affiliations/:id" component={UsersPageContainer} />
                      }
                    />

                    <Route
                      path="/find-by-phone/:phone?"
                      element={<AppRouteElement routePath="/find-by-phone/:phone?" component={FindByPhone} />}
                    />

                    <Route
                      path="/users/:id_user/custom-offers/create"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/custom-offers/create"
                          component={CustomOffersCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/custom-offers/:offer_id"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/custom-offers/:offer_id"
                          component={CustomOffersEditPage}
                        />
                      }
                    />

                    <Route
                      path="/users/:id_user/credits"
                      element={<AppRouteElement routePath="/users/:id_user/credits" component={CreditsContainer} />}
                    />
                    <Route
                      path="/users/:user_email"
                      element={<AppRouteElement routePath="/users/:user_email" component={UserPage} />}
                    />
                    <Route
                      path="/users/:id_user/history"
                      element={<AppRouteElement routePath="/users/:id_user/history" component={UserHistory} />}
                    />
                    <Route
                      path="/users/:id_user/new-order/:step?"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/new-order/:step?"
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_OFFER} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/new-flight-order/:step?"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/new-flight-order/:step?"
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_OFFLINE_FLIGHT} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/new-gift-card-order/:step?"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/new-gift-card-order/:step?"
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_GIFT_CARD} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/new-insurance-order/:step?"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/new-insurance-order/:step?"
                          render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_INSURANCE} {...props} />}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/edit-orders/:id_orders/add-item/:step?"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/edit-orders/:id_orders/add-item/:step?"
                          component={AddItemPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/edit-orders/:id_order/insurance/:id/:step"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/edit-orders/:id_order/insurance/:id/:step"
                          component={UpdateInsuranceContainer}
                        />
                      }
                    />
                    <Route
                      path="/users/:id_user/atol"
                      element={<AppRouteElement routePath="/users/:id_user/atol" component={AtolPage} />}
                    />
                    <Route
                      path="/atol-certificate/:id_atol_certificate"
                      element={
                        <AppRouteElement
                          routePath="/atol-certificate/:id_atol_certificate"
                          component={AtolCertificateDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/atol-certificate/:id_atol_certificate/edit"
                      element={
                        <AppRouteElement
                          routePath="/atol-certificate/:id_atol_certificate/edit"
                          component={AtolCertificateEditPage}
                        />
                      }
                    />

                    <Route
                      path="/purchases"
                      element={<AppRouteElement routePath="/purchases" component={PurchasesPageContainer} />}
                    />
                    <Route
                      path="/purchases/section/:section"
                      element={
                        <AppRouteElement routePath="/purchases/section/:section" component={PurchasesPageContainer} />
                      }
                    />
                    <Route
                      path="/purchases/booking-number/:bookingNumber"
                      element={
                        <AppRouteElement
                          routePath="/purchases/booking-number/:bookingNumber"
                          component={PurchasesPageBookingNumberContainer}
                        />
                      }
                    />

                    <Route
                      path="/purchases/:id_orders/payment"
                      element={
                        <AppRouteElement routePath="/purchases/:id_orders/payment" component={OrderPaymentSurcharge} />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/gift/edit"
                      element={
                        <AppRouteElement routePath="/purchases/:id_orders/gift/edit" component={OrderGiftEditPage} />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/notes/add"
                      element={
                        <AppRouteElement routePath="/purchases/:id_orders/notes/add" component={OrderAddNotePage} />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/booking-details/*"
                      element={
                        <AppRouteElement
                          routePath={'/purchases/:id_orders/booking-details/*'}
                          component={OrderBookingContainer}
                        />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/atol-details/*"
                      element={
                        <AppRouteElement
                          routePath={'/purchases/:id_orders/atol-details/*'}
                          component={OrderAtolPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/financial-records"
                      element={
                        <AppRouteElement
                          routePath="/purchases/:id_orders/financial-records"
                          component={OrderFinancialRecordsPage}
                        />
                      }
                    />
                    <Route
                      path="/purchases/:id_orders/*"
                      element={
                        <AppRouteElement routePath={'/purchases/:id_orders/*'} component={OrderDetailContainer} />
                      }
                    />
                    <Route
                      path="/edit-purchases/:id_orders"
                      element={
                        <AppRouteElement routePath="/edit-purchases/:id_orders" component={OrderEditContainer} />
                      }
                    />
                    <Route
                      path="/system/users/:id_user"
                      element={
                        <AppRouteElement
                          routePath="/system/users/:id_user"
                          render={({ match, location }) => (
                            <Navigate to={`/users/${match.params.id_user}`} state={location.state} replace />
                          )}
                        />
                      }
                    />
                    <Route
                      path="/system"
                      element={<AppRouteElement routePath="/system" component={SystemPageContainer} />}
                    />
                    <Route
                      path="/system/terms-and-conditions"
                      element={
                        <AppRouteElement routePath="/system/terms-and-conditions" component={TermsAndConditionsPage} />
                      }
                    />
                    <Route
                      path="/marketing/terms-and-conditions"
                      element={<Navigate to="/system/terms-and-conditions" replace />}
                    />
                    <Route
                      path="/system/find-similar-accounts"
                      element={
                        <AppRouteElement
                          routePath="/system/find-similar-accounts"
                          component={FindSimilarAccountsPage}
                        />
                      }
                    />
                    <Route
                      path="/system/account-deletions"
                      element={
                        <AppRouteElement routePath="/system/account-deletions" component={AccountDeletionsPage} />
                      }
                    />
                    <Route
                      path="/system/bulk-credit-allocation"
                      element={
                        <AppRouteElement
                          routePath="/system/bulk-credit-allocation"
                          component={BulkCreditAllocationPage}
                        />
                      }
                    />
                    <Route
                      path="/system/bulk-vcc-replacement"
                      element={
                        <AppRouteElement routePath="/system/bulk-vcc-replacement" component={BulkVccReplacementPage} />
                      }
                    />
                    <Route
                      path="/system/credit-reversals"
                      element={<AppRouteElement routePath="/system/credit-reversals" component={CreditReversalsPage} />}
                    />
                    <Route
                      path="/system/sale-ownership-swaps"
                      element={
                        <AppRouteElement routePath="/system/sale-ownership-swaps" component={SaleOwnershipSwapsPage} />
                      }
                    />
                    <Route
                      path="/system/cogs-adjustment-requests"
                      element={
                        <AppRouteElement
                          routePath="/system/cogs-adjustment-requests"
                          component={CogsAdjustmentRequestsPage}
                        />
                      }
                    />
                    <Route
                      path="/system/email-delivery-failures"
                      element={
                        <AppRouteElement
                          routePath="/system/email-delivery-failures"
                          component={EmailDeliveryFailuresPage}
                        />
                      }
                    />
                    <Route
                      path="/system/cs-analysis"
                      element={<AppRouteElement routePath="/system/cs-analysis" component={CSAnalysisPage} />}
                    />
                    <Route
                      path="/system/email-ai-kill-switches"
                      element={
                        <AppRouteElement
                          routePath="/system/email-ai-kill-switches"
                          component={EmailAIKillSwitchesPage}
                        />
                      }
                    />
                    <Route
                      path="/system/le-data-gallery-backfill"
                      element={
                        <AppRouteElement
                          routePath="/system/le-data-gallery-backfill"
                          component={LeDataGalleryBackfillPage}
                        />
                      }
                    />
                    <Route
                      path="/system/sierra-email-ai-kill-switch"
                      element={<Navigate to="/system/email-ai-kill-switches" replace />}
                    />
                    <Route
                      path="/system/intent-routing-kill-switch"
                      element={<Navigate to="/system/email-ai-kill-switches" replace />}
                    />
                    <Route
                      path="/system/admin-users"
                      element={<AppRouteElement routePath="/system/admin-users" component={AdminUsersPageContainer} />}
                    />
                    <Route
                      path="/system/stop-list"
                      element={<AppRouteElement routePath="/system/stop-list" component={StoplistPage} />}
                    />
                    <Route
                      path="/system/reservation-remediation"
                      element={
                        <AppRouteElement
                          routePath="/system/reservation-remediation"
                          component={ReservationRemediation}
                        />
                      }
                    />
                    <Route
                      path="/system/audit-logs"
                      element={<AppRouteElement routePath="/system/audit-logs" component={AuditLogSearch} />}
                    />
                    <Route
                      path="/system/permissions/:role"
                      element={
                        <AppRouteElement routePath="/system/permissions/:role" component={PermissionsContainer} />
                      }
                    />
                    <Route
                      path="/system/settings"
                      element={<AppRouteElement routePath="/system/settings" component={Settings} />}
                    />
                    <Route
                      path="/system/image-uploader"
                      element={
                        <AppRouteElement routePath="/system/image-uploader" component={ImageUploaderUtilityPage} />
                      }
                    />
                    <Route
                      path="/system/ai-image-manager/:tab?/:batchId?"
                      element={
                        <AppRouteElement
                          routePath="/system/ai-image-manager/:tab?/:batchId?"
                          component={AIImageManagerPage}
                        />
                      }
                    />
                    <Route
                      path="/system/sales-fx-hedging"
                      element={<AppRouteElement routePath="/system/sales-fx-hedging" component={SalesFxHedging} />}
                    />
                    <Route
                      path="/system/media-package-builder"
                      element={<Navigate replace to="/marketing/media-package-builder" />}
                    />
                    <Route
                      path="/system/media-package-builder/assets"
                      element={
                        <AppRouteElement routePath="/system/media-package-builder/assets" component={MediaAssetsPage} />
                      }
                    />
                    <Route
                      path="/system/media-package-builder/assets/new"
                      element={
                        <AppRouteElement
                          routePath="/system/media-package-builder/assets/new"
                          component={MediaAssetCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/system/media-package-builder/assets/:assetId"
                      element={
                        <AppRouteElement
                          routePath="/system/media-package-builder/assets/:assetId"
                          component={MediaAssetDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/system/media-package-builder/packages"
                      element={
                        <AppRouteElement
                          routePath="/system/media-package-builder/packages"
                          component={MediaPackagesPage}
                        />
                      }
                    />
                    <Route
                      path="/system/media-package-builder/packages/:mediaPackageId"
                      element={
                        <AppRouteElement
                          routePath="/system/media-package-builder/packages/:mediaPackageId"
                          component={MediaPackageDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/system/targeted-banner-campaigns"
                      element={
                        <AppRouteElement
                          routePath="/system/targeted-banner-campaigns"
                          component={SiteBannerCampaignsPage}
                        />
                      }
                    />
                    <Route
                      path="/system/targeted-banner-campaigns/create"
                      element={
                        <AppRouteElement
                          routePath="/system/targeted-banner-campaigns/create"
                          component={SiteBannerCampaignCreate}
                        />
                      }
                    />
                    <Route
                      path="/system/targeted-banner-campaigns/:campaignId"
                      element={
                        <AppRouteElement
                          routePath="/system/targeted-banner-campaigns/:campaignId"
                          component={SiteBannerCampaignEdit}
                        />
                      }
                    />

                    {!isProduction && (
                      <Route
                        path="/system/training-account-provisioning"
                        element={
                          <AppRouteElement
                            routePath="/system/training-account-provisioning"
                            component={TrainingAccountProvisioningPage}
                          />
                        }
                      />
                    )}
                    {!isProduction && (
                      <Route
                        path="/system/cs-new-hire-quiz"
                        element={<AppRouteElement routePath="/system/cs-new-hire-quiz" component={CSNewHireQuizPage} />}
                      />
                    )}
                    <Route
                      path="/vendors"
                      element={<AppRouteElement routePath="/vendors" component={VendorsPageContainer} />}
                    />
                    <Route
                      path="/vendors/billing-rules"
                      element={
                        <AppRouteElement
                          routePath="/vendors/billing-rules"
                          // Gated to admin + employee, matching the nav sublink and the svc-vendor API;
                          // without this a hotel-coordinator could still reach the page by direct URL.
                          render={() => (
                            <PermissionedComponent
                              requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}
                              fallback={billingRulesDenied}
                            >
                              <VendorBillingRulesPage />
                            </PermissionedComponent>
                          )}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor"
                      element={<AppRouteElement routePath="/vendors/:id_vendor" component={VendorsDetailContainer} />}
                    />
                    <Route
                      path="/vendors/:id_vendor/partner-central"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/partner-central"
                          component={VendorsPartnerCentralPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/chain/:id_parent"
                      element={
                        <AppRouteElement routePath="/vendors/chain/:id_parent" component={ChainsDetailContainer} />
                      }
                    />
                    <Route
                      path="/vendors/chain/:id_parent/settings"
                      element={
                        <AppRouteElement
                          routePath="/vendors/chain/:id_parent/settings"
                          component={EditChainSettingsPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties"
                      element={
                        <AppRouteElement routePath="/vendors/:id_vendor/properties" component={PropertyNewContainer} />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property"
                          component={PropertyPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/edit-property/:id_property"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/edit-property/:id_property"
                          component={PropertyEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/inclusions"
                      element={
                        <AppRouteElement routePath="/vendors/:id_vendor/inclusions" component={VendorInclusionsPage} />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans-groups"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/rate-plans-groups"
                          component={RatePlansGroupEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans"
                      element={
                        <AppRouteElement routePath="/vendors/:id_vendor/rate-plans" component={RatePlanNewContainer} />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans/:id_rate_plan"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/rate-plans/:id_rate_plan"
                          component={RatePlanPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/rate-plans/:id_rate_plan/inclusions"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/rate-plans/:id_rate_plan/inclusions"
                          component={RatePlanInclusionsPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/edit-rate-plan/:id_rate_plan"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/edit-rate-plan/:id_rate_plan"
                          component={RatePlanEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-curated-content"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-curated-content"
                          component={CuratedContentEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-room-types"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-room-types"
                          component={RoomTypesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-images"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-images"
                          component={ImagesEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-videos"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-videos"
                          component={VideosEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate"
                          component={RoomTypeEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate/dates"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate/dates"
                          component={RoomTypeDatesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/room-types-groups"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/room-types-groups"
                          component={RoomTypesGroupsPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-room-types-groups"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-room-types-groups"
                          component={RoomTypesGroupEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-reviews"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-property-reviews"
                          component={PropertyReviewsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-content"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-content"
                          component={PropertyTaxesContentEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-commissionable-taxes-content"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-property-commissionable-taxes-content"
                          component={PropertyCommissionableTaxesContentEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-fees"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-fees"
                          component={PropertyTaxesFeesEditPage}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour"
                      element={
                        <AppRouteElement routePath="/vendors/:id_vendor/tours/:id_tour" component={TourPageContainer} />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/edit-tour/:id_tour"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/edit-tour/:id_tour"
                          component={TourEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours"
                      element={<AppRouteElement routePath="/vendors/:id_vendor/tours" component={TourNewContainer} />}
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-options"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/tours/:id_tour/edit-tour-options"
                          component={TourOptionsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/tour-options/:id_tour_option/dates"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/tours/:id_tour/tour-options/:id_tour_option/dates"
                          component={TourOptionDatesEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-legs"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/tours/:id_tour/edit-tour-legs"
                          component={TourLegsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-reviews"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/tours/:id_tour/edit-tour-reviews"
                          component={TourReviewsEditContainer}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/addons/:id_addon"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/addons/:id_addon"
                          component={VendorAddonDetails}
                        />
                      }
                    />
                    <Route
                      path="/vendors/:id_vendor/addons/:id_addon/deal-options/:id_addon_deal_option/edit"
                      element={
                        <AppRouteElement
                          routePath="/vendors/:id_vendor/addons/:id_addon/deal-options/:id_addon_deal_option/edit"
                          component={VendorAddonEdit}
                        />
                      }
                    />

                    <Route
                      path="/bookings/:id_orders"
                      element={<AppRouteElement routePath="/bookings/:id_orders" component={BookingPage} />}
                    />

                    <Route
                      path="/marketing/voucher-display-config/new"
                      element={
                        <AppRouteElement
                          routePath="/marketing/voucher-display-config/new"
                          component={VoucherDisplayConfigDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/voucher-display-config/:voucherDisplayConfigId"
                      element={
                        <AppRouteElement
                          routePath="/marketing/voucher-display-config/:voucherDisplayConfigId"
                          component={VoucherDisplayConfigDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/vouchers/new"
                      element={<AppRouteElement routePath="/marketing/vouchers/new" component={VoucherFormPage} />}
                    />
                    <Route
                      path="/marketing/vouchers/quick-create"
                      element={
                        <AppRouteElement
                          routePath="/marketing/vouchers/quick-create"
                          component={QuickCreateVoucherPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/vouchers/:voucherId"
                      element={
                        <AppRouteElement routePath="/marketing/vouchers/:voucherId" component={VoucherFormPage} />
                      }
                    />
                    <Route
                      path="/marketing/voucher-auto-assign-rules/create"
                      element={
                        <AppRouteElement
                          routePath="/marketing/voucher-auto-assign-rules/create"
                          render={() => (isPromoAdmin ? <VoucherAutoAssignRuleFormPage /> : promoAdminOnlyDenied)}
                        />
                      }
                    />
                    <Route
                      path="/marketing/voucher-auto-assign-rules/group/:assignmentGroup"
                      element={
                        <AppRouteElement
                          routePath="/marketing/voucher-auto-assign-rules/group/:assignmentGroup"
                          render={() => (isPromoAdmin ? <VoucherAutoAssignRuleGroupPage /> : promoAdminOnlyDenied)}
                        />
                      }
                    />
                    <Route
                      path="/marketing/voucher-auto-assign-rules/:ruleId"
                      element={
                        <AppRouteElement
                          routePath="/marketing/voucher-auto-assign-rules/:ruleId"
                          render={() => (isPromoAdmin ? <VoucherAutoAssignRuleFormPage /> : promoAdminOnlyDenied)}
                        />
                      }
                    />
                    <Route
                      path="/marketing/channel-markup-v2/:channelMarkupId/edit"
                      element={
                        <AppRouteElement
                          routePath="/marketing/channel-markup-v2/:channelMarkupId/edit"
                          component={ChannelMarkupCreateV2Page}
                        />
                      }
                    />
                    <Route
                      path="/marketing/channel-markup-v2/create"
                      element={
                        <AppRouteElement
                          routePath="/marketing/channel-markup-v2/create"
                          component={ChannelMarkupCreateV2Page}
                        />
                      }
                    />
                    <Route
                      path="/marketing/channel-markup/create"
                      element={
                        <AppRouteElement
                          routePath="/marketing/channel-markup/create"
                          component={ChannelMarkupCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/audiences/create"
                      element={
                        <AppRouteElement routePath="/marketing/audiences/create" component={AudienceAdminPage} />
                      }
                    />
                    <Route
                      path="/marketing/audiences/:audienceId/edit"
                      element={
                        <AppRouteElement
                          routePath="/marketing/audiences/:audienceId/edit"
                          component={AudienceAdminPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/marketing-router-rules/compare"
                      element={
                        <AppRouteElement
                          routePath="/marketing/marketing-router-rules/compare"
                          component={MarketingRouterRuleComparisonPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/marketing-router-rules/create"
                      element={
                        <AppRouteElement
                          routePath="/marketing/marketing-router-rules/create"
                          component={MarketingRouterRuleFormPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/marketing-router-rules/:marketingRouterId/edit"
                      element={
                        <AppRouteElement
                          routePath="/marketing/marketing-router-rules/:marketingRouterId/edit"
                          component={MarketingRouterRuleFormPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/marketing-router-rules/:marketingRouterId"
                      element={
                        <AppRouteElement
                          routePath="/marketing/marketing-router-rules/:marketingRouterId"
                          component={MarketingRouterRuleDetailPage}
                        />
                      }
                    />
                    <Route
                      path="/marketing/tag-reports/export"
                      element={<AppRouteElement routePath="/marketing/tag-reports/export" component={TagDataExport} />}
                    />
                    <Route
                      path="/marketing/:tab?"
                      element={<AppRouteElement routePath="/marketing/:tab?" component={MarketingPage} />}
                    />
                    <Route
                      path="/marketing/google-ads/customer/"
                      element={
                        <AppRouteElement routePath="/marketing/google-ads/customer/" component={CustomerListView} />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/creative-ai"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/creative-ai"
                          component={AdCreativeAIAssistantContainer}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/bid-modification-history/"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/bid-modification-history/"
                          component={BidModificationHistory}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/hotel-bid-modification-history/"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/hotel-bid-modification-history/"
                          component={HotelBidModificationHistory}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/hotel-bid-failure-log"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/hotel-bid-failure-log"
                          component={HotelBidFailureLog}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/customer/:customerId/campaign"
                          component={CustomerCampaignListView}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/customer/:customerId/campaign/:campaignId"
                          component={CampaignDetailsView}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group"
                          component={AdGroupListView}
                        />
                      }
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group/:adGroupId"
                      element={
                        <AppRouteElement
                          routePath="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group/:adGroupId"
                          component={AdGroupDetailsView}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/properties"
                      element={
                        <AppRouteElement routePath="/accommodation/properties" component={AccommodationHomePage} />
                      }
                    />
                    <Route
                      path="/accommodation/properties/:propertyId/*"
                      element={
                        <AppRouteElement
                          routePath={'/accommodation/properties/:propertyId/*'}
                          component={AccommodationPropertyDetailsPage}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/bundles/new"
                      element={
                        <AppRouteElement
                          routePath="/accommodation/bundles/new"
                          component={AccommodationBundleCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/bundles/:bundleId/:tab?/*"
                      element={
                        <AppRouteElement
                          routePath={'/accommodation/bundles/:bundleId/:tab?/*'}
                          component={AccommodationBundleEditPage}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/campaigns"
                      element={
                        <AppRouteElement
                          routePath="/accommodation/campaigns"
                          component={AccommodationCampaignsContainer}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/campaigns/new"
                      element={
                        <AppRouteElement
                          routePath="/accommodation/campaigns/new"
                          component={AccommodationCampaignCreatePage}
                        />
                      }
                    />
                    <Route
                      path="/accommodation/campaigns/:campaignId/:tab?/*"
                      element={
                        <AppRouteElement
                          routePath={'/accommodation/campaigns/:campaignId/:tab?/*'}
                          component={AccommodationCampaignEditPage}
                        />
                      }
                    />
                    <Route
                      path="/email/render"
                      element={<AppRouteElement routePath="/email/render" component={RenderEmail} />}
                    />
                    <Route
                      path="/email/sent"
                      element={<AppRouteElement routePath="/email/sent" component={EmailPageContainer} />}
                    />

                    <Route
                      path="/comped/:id_comped"
                      element={
                        <AppRouteElement routePath="/comped/:id_comped" component={CompedPackagesDetailContainer} />
                      }
                    />
                    <Route
                      path="/promos/code/:id_code"
                      element={<AppRouteElement routePath="/promos/code/:id_code" component={PromoDetailContainerV3} />}
                    />
                    <Route
                      path="/promos/:id_promo"
                      element={<AppRouteElement routePath="/promos/:id_promo" component={PromoDetailContainerV3} />}
                    />
                    <Route
                      path="/badges"
                      element={<AppRouteElement routePath="/badges" component={BadgesContainer} />}
                    />
                    <Route
                      path="/badges/:id_badge"
                      element={<AppRouteElement routePath="/badges/:id_badge" component={BadgesDetailContainer} />}
                    />

                    <Route path="/profile" element={<AppRouteElement routePath="/profile" component={ProfilePage} />} />
                    <Route path="/loyalty" element={<Navigate to="/loyalty/societe/campaigns" replace />} />
                    <Route
                      path="/loyalty/:group/:section"
                      element={
                        <AppRouteElement routePath="/loyalty/:group/:section" component={LoyaltyPageContainer} />
                      }
                    />
                    {/* Legacy links into the old flat /societe/:section URLs. */}
                    <Route path="/societe" element={<Navigate to="/loyalty/societe/campaigns" replace />} />
                    <Route
                      path="/societe/:section"
                      element={
                        <AppRouteElement
                          routePath="/societe/:section"
                          render={({ match, location }) => (
                            <Navigate to={`/loyalty/societe/${match.params.section}${location.search}`} replace />
                          )}
                        />
                      }
                    />
                    <Route
                      path="/finance"
                      element={<AppRouteElement routePath="/finance" component={FinancePageContainer} />}
                    />
                    <Route
                      path="/finance/offers/:id_offer"
                      element={
                        <AppRouteElement
                          routePath="/finance/offers/:id_offer"
                          component={FinanceOfferDetailContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/offers/:id_offer/offer-payments/:id_vendor/:id_offer_payment"
                      element={
                        <AppRouteElement
                          routePath="/finance/offers/:id_offer/offer-payments/:id_vendor/:id_offer_payment"
                          component={VendorOfferPaymentsContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/offers/:id_offer/offer-payments/:id_vendor"
                      element={
                        <AppRouteElement
                          routePath="/finance/offers/:id_offer/offer-payments/:id_vendor"
                          component={VendorOfferPaymentsContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/offers/:id_offer/booking-payments/:id_vendor"
                      element={
                        <AppRouteElement
                          routePath="/finance/offers/:id_offer/booking-payments/:id_vendor"
                          component={VendorBookingPaymentsContainer}
                        />
                      }
                    />
                    <Route
                      path="/finance/:section/:report/*"
                      element={
                        <AppRouteElement routePath={'/finance/:section/:report/*'} component={FinancePageContainer} />
                      }
                    />
                    <Route
                      path="/finance/:section/*"
                      element={<AppRouteElement routePath={'/finance/:section/*'} component={FinancePageContainer} />}
                    />

                    <Route path="/email" element={<AppRouteElement routePath="/email" component={EmailPage} />} />
                    <Route
                      path="/email/:tab?"
                      element={<AppRouteElement routePath="/email/:tab?" component={EmailPage} />}
                    />

                    <Route
                      path="/customer-communication/hero-planner/schedules/:scheduleEditorId"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/hero-planner/schedules/:scheduleEditorId"
                          component={HeroPlannerScheduleEditorPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/hero-planner/schedules"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/hero-planner/schedules"
                          component={HeroPlannerSchedulesListPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/translation-overview"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/translation-overview"
                          component={TranslationOverviewPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/translation-catalog"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/translation-catalog"
                          component={TranslationCatalogPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/banner-upload"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/banner-upload"
                          component={BannerUploadPage}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/email/sent"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/email/sent"
                          component={MarketingEmailsPageContainer}
                        />
                      }
                    />
                    <Route
                      path="/customer-communication/email/render"
                      element={
                        <AppRouteElement
                          routePath="/customer-communication/email/render"
                          component={RenderMarketingEmailPage}
                        />
                      }
                    />

                    <Route path="/health" element={<AppRouteElement routePath="/health" component={HealthPage} />} />

                    <Route
                      path="/cruises/rate-grouping/rate-assignment-assistant"
                      element={
                        <AppRouteElement
                          routePath="/cruises/rate-grouping/rate-assignment-assistant"
                          component={RateGroupingManagementPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/rate-grouping-management"
                      element={
                        <Navigate
                          to={{
                            pathname: '/cruises/rate-grouping/rate-assignment-assistant',
                            search: location.search,
                          }}
                          replace
                        />
                      }
                    />
                    <Route
                      path="/cruises/:section"
                      element={<AppRouteElement routePath="/cruises/:section" component={CruisesPage} />}
                    />
                    <Route
                      path="/cruises/offer/:id_offer"
                      element={<AppRouteElement routePath="/cruises/offer/:id_offer" component={CruisePageDetails} />}
                    />
                    <Route
                      path="/cruises/offer/:offerId/images"
                      element={
                        <AppRouteElement routePath="/cruises/offer/:offerId/images" component={CruisePageImages} />
                      }
                    />
                    <Route
                      path="/lere"
                      element={<AppRouteElement routePath="/lere" component={LereAdminPageContainer} />}
                    />
                    <Route
                      path="/lere/:section"
                      element={<AppRouteElement routePath="/lere/:section" component={LereAdminPageContainer} />}
                    />
                    <Route
                      path="/cruises/ships/:shipId"
                      element={<AppRouteElement routePath="/cruises/ships/:shipId" component={ShipImageEditor} />}
                    />
                    <Route
                      path="/cruises/ships/:shipId/images"
                      element={
                        <AppRouteElement routePath="/cruises/ships/:shipId/images" component={ShipImageEditor} />
                      }
                    />
                    <Route
                      path="/cruises/ships/:shipId/amenities"
                      element={
                        <AppRouteElement routePath="/cruises/ships/:shipId/amenities" component={ShipImageEditor} />
                      }
                    />
                    <Route
                      path="/cruises/cruise-lines/order"
                      element={
                        <AppRouteElement routePath="/cruises/cruise-lines/order" component={CruiseLineOrdering} />
                      }
                    />
                    <Route
                      path="/cruises/cruise-lines/:cruiseLineId"
                      element={
                        <AppRouteElement routePath="/cruises/cruise-lines/:cruiseLineId" component={CruiseLineEdit} />
                      }
                    />
                    <Route
                      path="/cruises/ports/:portId"
                      element={<AppRouteElement routePath="/cruises/ports/:portId" component={CruisePortsEditPage} />}
                    />
                    <Route
                      path="/cruises/rate-code-management"
                      element={
                        <AppRouteElement routePath="/cruises/rate-code-management" component={RateCodeManagementPage} />
                      }
                    />
                    <Route
                      path="/cruises/rate-code-management/:rateCodeId/edit"
                      element={
                        <AppRouteElement
                          routePath="/cruises/rate-code-management/:rateCodeId/edit"
                          component={EditRateCodePage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/sailings/:id_sailing"
                      element={
                        <AppRouteElement
                          routePath="/cruises/sailings/:id_sailing"
                          component={CruiseSailingDetailsPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/cabin-categories/:id_cabin"
                      element={
                        <AppRouteElement
                          routePath="/cruises/cabin-categories/:id_cabin"
                          component={CruiseCabinCategoriesDetailsPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/cabin-categories-groups/create"
                      element={
                        <AppRouteElement
                          routePath="/cruises/cabin-categories-groups/create"
                          component={CreateCabinCategoriesGroupPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/create"
                      element={<AppRouteElement routePath="/cruises/bundles/create" component={CruiseBundlesCreate} />}
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId"
                      element={
                        <AppRouteElement routePath="/cruises/bundles/edit/:bundleId" component={CruiseBundlesEdit} />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/items"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/create"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/items/create"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/items/create/:actionId"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/edit"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/items/edit"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/items/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/items/edit/:actionId"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/filters"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/create"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/filters/create"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/filters/create/:actionId"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/edit"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/filters/edit"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bundles/edit/:bundleId/filters/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bundles/edit/:bundleId/filters/edit/:actionId"
                          component={CruiseBundlesEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/create"
                      element={
                        <AppRouteElement routePath="/cruises/inclusions/create" component={CruiseInclusionsCreate} />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/items"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/create"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/items/create"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/items/create/:actionId"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/edit"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/items/edit"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/items/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/items/edit/:actionId"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/filters"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/create"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/filters/create"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/create/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/filters/create/:actionId"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/edit"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/filters/edit"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/inclusions/edit/:inclusionId/filters/edit/:actionId"
                      element={
                        <AppRouteElement
                          routePath="/cruises/inclusions/edit/:inclusionId/filters/edit/:actionId"
                          component={CruiseInclusionsEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/deals"
                      element={<AppRouteElement routePath="/cruises/deals" component={DealPageList} />}
                    />
                    <Route
                      path="/cruises/deals/create"
                      element={<AppRouteElement routePath="/cruises/deals/create" component={DealPageCreate} />}
                    />
                    <Route
                      path="/cruises/deals/ordering"
                      element={<AppRouteElement routePath="/cruises/deals/ordering" component={DealPageOrdering} />}
                    />
                    <Route
                      path="/cruises/deals/:id/edit"
                      element={<AppRouteElement routePath="/cruises/deals/:id/edit" component={DealPageEdit} />}
                    />
                    <Route
                      path="/cruises/deals/:id/edit/inclusions/:action?"
                      element={
                        <AppRouteElement
                          routePath="/cruises/deals/:id/edit/inclusions/:action?"
                          component={DealPageEdit}
                        />
                      }
                    />
                    <Route
                      path="/cruises/cabin-categories-groups/:id_group"
                      element={
                        <AppRouteElement
                          routePath="/cruises/cabin-categories-groups/:id_group"
                          component={CabinCategoriesGroupOverviewPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/bookings/:booking_id/logs"
                      element={
                        <AppRouteElement
                          routePath="/cruises/bookings/:booking_id/logs"
                          component={CruisesBookingLogsPage}
                        />
                      }
                    />
                    <Route
                      path="/cruises/booking-logs"
                      element={<AppRouteElement routePath="/cruises/booking-logs" component={CruisesBookingsPage} />}
                    />
                    <Route
                      path="/cruises/deal-crawler/create"
                      element={
                        <AppRouteElement routePath="/cruises/deal-crawler/create" component={CruiseDealCrawlerCreate} />
                      }
                    />
                    <Route
                      path="/cruises/deal-crawler/:id/view"
                      element={
                        <AppRouteElement routePath="/cruises/deal-crawler/:id/view" component={CruiseDealCrawlerView} />
                      }
                    />
                    <Route
                      path={DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}
                      element={
                        <AppRouteElement
                          routePath={DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}
                          component={DealCrawlerPageCreationSelection}
                        />
                      }
                    />
                    <Route
                      path={DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}
                      element={
                        <AppRouteElement
                          routePath={DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}
                          component={DealCrawlerPageCreationConfirm}
                        />
                      }
                    />

                    <Route
                      path="/lebusiness"
                      element={<AppRouteElement routePath="/lebusiness" component={BusinessMainPage} />}
                    />
                    <Route
                      path="/lebusiness/:businessId/:tab"
                      element={
                        <AppRouteElement routePath="/lebusiness/:businessId/:tab" component={BusinessTabContainer} />
                      }
                    />

                    <Route path="/invoice-tools" element={<Navigate to="/invoice-tools/invoice-generator" replace />} />
                    <Route
                      path="/invoice-tools/:tab"
                      element={
                        <AppRouteElement routePath="/invoice-tools/:tab" component={InvoiceToolsPageContainer} />
                      }
                    />

                    <Route
                      path="/support-assistant/:tab?"
                      element={
                        <AppRouteElement routePath="/support-assistant/:tab?" component={SupportAssistantPage} />
                      }
                    />

                    <Route
                      path="/amenities"
                      element={<AppRouteElement routePath="/amenities" component={AmenitiesPageContainer} />}
                    />
                    <Route
                      path="/amenities/:id_amenity"
                      element={<AppRouteElement routePath="/amenities/:id_amenity" component={AmenityEdit} />}
                    />
                    <Route
                      path="/amenity-groups/:id_group"
                      element={<AppRouteElement routePath="/amenity-groups/:id_group" component={AmenityGroupEdit} />}
                    />

                    <Route
                      path="/commercial-ops/price-parity/scheduler/jobs/:jobId"
                      element={
                        <AppRouteElement
                          routePath="/commercial-ops/price-parity/scheduler/jobs/:jobId"
                          component={ScheduledPriceSummaryJobDetails}
                        />
                      }
                    />
                    <Route
                      path="/commercial-ops/chains/:chainId"
                      element={
                        <AppRouteElement routePath="/commercial-ops/chains/:chainId" component={ChainDetailPage} />
                      }
                    />
                    <Route
                      path="/commercial-ops/:section?/:subsection?"
                      element={
                        <AppRouteElement
                          routePath="/commercial-ops/:section?/:subsection?"
                          component={CommercialOpsPage}
                        />
                      }
                    />

                    <Route
                      path="/sales-performance-dashboard/:tab?"
                      element={
                        <AppRouteElement
                          routePath="/sales-performance-dashboard/:tab?"
                          component={SalesPerformanceDashboardPage}
                        />
                      }
                    />

                    <Route
                      path="/opportunity/:opportunityId"
                      element={
                        <AppRouteElement routePath="/opportunity/:opportunityId" component={OpportunityContainer} />
                      }
                    />

                    <Route path="/example" element={<AppRouteElement routePath="/example" component={ExamplePage} />} />
                    <Route
                      path="/site-banner-campaigns"
                      element={
                        <AppRouteElement routePath="/site-banner-campaigns" component={SiteBannerCampaignsPage} />
                      }
                    />
                    <Route
                      path="/site-banner-campaigns/create"
                      element={
                        <AppRouteElement
                          routePath="/site-banner-campaigns/create"
                          component={SiteBannerCampaignCreate}
                        />
                      }
                    />
                    <Route
                      path="/site-banner-campaigns/:campaignId"
                      element={
                        <AppRouteElement
                          routePath="/site-banner-campaigns/:campaignId"
                          component={SiteBannerCampaignEdit}
                        />
                      }
                    />
                    <Route
                      path="/cx-dashboard"
                      element={<AppRouteElement routePath="/cx-dashboard" component={CxDashboardPage} />}
                    />
                    <Route
                      path="/ai-qa"
                      element={<AppRouteElement routePath="/ai-qa" component={AiQaDashboardPage} />}
                    />
                    <Route
                      path="/system/cs-agent-actions-dashboard"
                      element={
                        <AppRouteElement routePath="/system/cs-agent-actions-dashboard" component={CsDashboardPage} />
                      }
                    />
                    <Route
                      path="/users/:id_user/offer-deep-link"
                      element={
                        <AppRouteElement
                          routePath="/users/:id_user/offer-deep-link"
                          component={CreateOfferDeepLinkForm}
                        />
                      }
                    />
                    <Route
                      path="/corporate-events/companies"
                      element={
                        <AppRouteElement routePath="/corporate-events/companies" component={CompaniesListPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/companies/create"
                      element={
                        <AppRouteElement routePath="/corporate-events/companies/create" component={CompanyFormPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/companies/:id/edit"
                      element={
                        <AppRouteElement routePath="/corporate-events/companies/:id/edit" component={CompanyFormPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/events"
                      element={<AppRouteElement routePath="/corporate-events/events" component={EventsListPage} />}
                    />
                    <Route
                      path="/corporate-events/events/create"
                      element={
                        <AppRouteElement routePath="/corporate-events/events/create" component={EventFormPage} />
                      }
                    />
                    <Route
                      path="/corporate-events/events/:id/edit"
                      element={
                        <AppRouteElement routePath="/corporate-events/events/:id/edit" component={EventFormPage} />
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </RouteScanner>
              </SpotlightProvider>
            </AiChatEntry>
          </Authenticated>

          <NotAuthenticated>
            <Routes>
              <Route path="*" element={<AppRouteElement routePath="*" component={LoginPage} />} />
            </Routes>
          </NotAuthenticated>
        </Box>

        {isAskRichardRoute ? null : <Footer />}
      </AppChrome>
    </Box>
  );
}
